export interface ApiErrorMessageOptions {
  fallback?: string;
  statusFallbacks?: Partial<Record<number, string>>;
  /** Shown when API returns SQL/duplicate-key errors instead of a readable message. */
  duplicateEntryMessage?: string;
  /** Maps role id from role-assignment errors to a display name. */
  resolveRoleName?: (roleId: number) => string;
}

interface ApiErrorBody {
  data?: {
    details?: unknown[];
    detail?: string;
    message?: string;
  };
  details?: unknown[];
  detail?: string;
  message?: string;
  title?: string;
  error?: string;
}

const GENERIC_API_MESSAGE_PATTERN =
  /^(error\.http\.\d{3}|http failure response|unknown error)$/i;

const INACTIVE_ROLE_MESSAGE_PATTERN =
  /role\s+not\s+found\s+or\s+inactive\s*:\s*(\d+)/i;

const ROLE_ASSIGNMENT_FAILED_PATTERN = /error\.roleassignmentfailed/i;

const SPRING_PROBLEM_DETAIL_PATTERN =
  /problemdetailwithcause|problem detail|error_key=/i;

const WEAK_VALIDATION_SUMMARY_PATTERN = /^(validation failed|bad request)$/i;

function isSpringProblemDetailMessage(message: string): boolean {
  return SPRING_PROBLEM_DETAIL_PATTERN.test(message);
}

function gatherSearchableErrorText(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return '';
  }

  const parts: string[] = [];
  const record = error as Record<string, unknown>;

  if (typeof record['message'] === 'string') {
    parts.push(record['message']);
  }

  const body = record['error'];
  if (typeof body === 'string') {
    parts.push(body);
  } else if (body && typeof body === 'object') {
    const bodyRecord = body as Record<string, unknown>;
    for (const key of ['message', 'detail', 'title', 'error']) {
      const value = bodyRecord[key];
      if (value != null && String(value).trim() && String(value) !== 'null') {
        parts.push(String(value));
      }
    }

    const properties = bodyRecord['properties'];
    if (properties && typeof properties === 'object') {
      const props = properties as Record<string, unknown>;
      for (const key of ['message', 'error_key', 'errorKey']) {
        const value = props[key];
        if (value != null) {
          parts.push(String(value));
        }
      }
    } else if (typeof properties === 'string') {
      parts.push(properties);
    }

    try {
      parts.push(JSON.stringify(body));
    } catch {
      // ignore circular refs
    }
  }

  return parts.join(' ');
}

function extractInactiveRoleId(text: string): number | null {
  const match = text.match(INACTIVE_ROLE_MESSAGE_PATTERN);
  if (!match) {
    return null;
  }
  const roleId = Number(match[1]);
  return Number.isFinite(roleId) && roleId > 0 ? roleId : null;
}

function tryResolveInactiveRoleAssignmentMessage(
  error: unknown,
  options: ApiErrorMessageOptions
): string | null {
  const searchable = gatherSearchableErrorText(error);
  if (!searchable.trim()) {
    return null;
  }

  const roleId = extractInactiveRoleId(searchable);
  const isRoleAssignmentFailure =
    ROLE_ASSIGNMENT_FAILED_PATTERN.test(searchable) || roleId != null;

  if (!isRoleAssignmentFailure || roleId == null) {
    return null;
  }

  const roleName = options.resolveRoleName?.(roleId)?.trim() || `Role ${roleId}`;
  return `Error: User Role "${roleName}" is currently InActive.`;
}

function isGenericApiMessage(message: string): boolean {
  const value = message.trim();
  if (!value) {
    return true;
  }
  return GENERIC_API_MESSAGE_PATTERN.test(value);
}

function isTechnicalDatabaseError(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('could not execute statement') ||
    lower.includes('duplicate entry') ||
    lower.includes('duplicate key') ||
    lower.includes('unique constraint') ||
    lower.includes('insert into ') ||
    /for key ['`]/.test(lower)
  );
}

function toUserFacingMessage(
  message: string,
  options: ApiErrorMessageOptions
): string {
  if (!message.trim()) {
    return '';
  }
  if (options.duplicateEntryMessage && isTechnicalDatabaseError(message)) {
    return options.duplicateEntryMessage;
  }
  if (isGenericApiMessage(message) || isTechnicalDatabaseError(message)) {
    return '';
  }
  if (isSpringProblemDetailMessage(message)) {
    return '';
  }
  return message.trim();
}

function isWeakValidationSummary(message: string): boolean {
  return WEAK_VALIDATION_SUMMARY_PATTERN.test(message.trim());
}

function collectFieldErrors(errors: unknown): string {
  if (!errors) {
    return '';
  }

  if (Array.isArray(errors)) {
    return errors
      .map((item) => {
        if (item == null) {
          return '';
        }
        if (typeof item === 'string') {
          return item.trim();
        }
        if (typeof item === 'object') {
          const record = item as Record<string, unknown>;
          const field = record['field'] ?? record['property'] ?? record['path'];
          const message =
            record['defaultMessage'] ??
            record['message'] ??
            record['detail'] ??
            record['description'] ??
            record['error'];
          const text = message == null ? '' : String(message).trim();
          if (!text) {
            return '';
          }
          return field != null && String(field).trim()
            ? `${String(field).trim()}: ${text}`
            : text;
        }
        return String(item).trim();
      })
      .filter(Boolean)
      .join('. ');
  }

  if (typeof errors === 'object') {
    return Object.entries(errors as Record<string, unknown>)
      .flatMap(([field, value]) => {
        if (Array.isArray(value)) {
          return value
            .map((entry) => String(entry).trim())
            .filter(Boolean)
            .map((entry) => `${field}: ${entry}`);
        }
        if (value != null && String(value).trim()) {
          return [`${field}: ${String(value).trim()}`];
        }
        return [];
      })
      .join('. ');
  }

  if (typeof errors === 'string') {
    return errors.trim();
  }

  return '';
}

function collectDetails(details: unknown[] | string | undefined): string {
  if (typeof details === 'string') {
    return details.trim();
  }
  if (!Array.isArray(details) || details.length === 0) {
    return '';
  }
  return details
    .map((item) => {
      if (item == null) {
        return '';
      }
      if (typeof item === 'string') {
        return item.trim();
      }
      if (typeof item === 'object') {
        const record = item as Record<string, unknown>;
        const candidate =
          record['message'] ??
          record['detail'] ??
          record['description'] ??
          record['error'];
        return candidate == null ? '' : String(candidate).trim();
      }
      return String(item).trim();
    })
    .filter(Boolean)
    .join('. ');
}

function unwrapErrorBody(error: unknown): ApiErrorBody | string | null {
  if (!error || typeof error !== 'object') {
    return null;
  }
  const record = error as Record<string, unknown>;
  const body = record['error'];
  if (typeof body === 'string') {
    return body;
  }
  if (!body || typeof body !== 'object') {
    return null;
  }
  return body as ApiErrorBody;
}

function extractMessageFromBody(
  body: ApiErrorBody,
  options: ApiErrorMessageOptions
): string {
  const nested = body.data ?? body;
  const nestedRecord =
    nested && typeof nested === 'object'
      ? (nested as Record<string, unknown>)
      : null;
  const bodyRecord = body as Record<string, unknown>;

  const fieldErrorsText = collectFieldErrors(
    nestedRecord?.['errors'] ?? bodyRecord['errors']
  );
  const fieldErrorsFacing = toUserFacingMessage(fieldErrorsText, options);
  if (fieldErrorsFacing) {
    return fieldErrorsFacing;
  }

  const detailsText = collectDetails(
    (nestedRecord?.['details'] ?? bodyRecord['details']) as
      | unknown[]
      | string
      | undefined
  );
  const detailsFacing = toUserFacingMessage(detailsText, options);
  if (detailsFacing) {
    return detailsFacing;
  }

  const candidates = [
    nestedRecord?.['detail'],
    bodyRecord['detail'],
    nestedRecord?.['message'],
    bodyRecord['message'],
    bodyRecord['title'],
    bodyRecord['error'],
  ];

  let weakSummary = '';
  for (const candidate of candidates) {
    if (candidate == null) {
      continue;
    }
    const facing = toUserFacingMessage(String(candidate), options);
    if (!facing) {
      continue;
    }
    if (isWeakValidationSummary(facing)) {
      weakSummary = facing;
      continue;
    }
    return facing;
  }

  return weakSummary;
}

/**
 * Resolves a user-facing API error message from HttpClient / interceptor errors.
 * Ignores generic backend keys such as `error.http.500`.
 */
export function resolveApiErrorMessage(
  error: unknown,
  options: ApiErrorMessageOptions = {}
): string {
  const fallback =
    options.fallback ?? 'Something went wrong. Please try again.';

  const record =
    error && typeof error === 'object'
      ? (error as Record<string, unknown>)
      : null;
  const status = Number(record?.['status']);

  if (status === 0) {
    return (
      'Unable to reach the server. Check your connection or sign in again and retry.'
    );
  }

  const inactiveRoleMessage = tryResolveInactiveRoleAssignmentMessage(
    error,
    options
  );
  if (inactiveRoleMessage) {
    return inactiveRoleMessage;
  }

  const body = unwrapErrorBody(error);
  if (typeof body === 'string') {
    const facing = toUserFacingMessage(body, options);
    if (facing) {
      return facing;
    }
  } else if (body) {
    const extracted = extractMessageFromBody(body, options);
    if (extracted) {
      return extracted;
    }
  }

  const topLevelMessage = record?.['message'];
  if (typeof topLevelMessage === 'string' && topLevelMessage.trim()) {
    const facing = toUserFacingMessage(topLevelMessage, options);
    if (facing) {
      return facing;
    }
  }

  if (
    options.duplicateEntryMessage &&
    ((typeof body === 'string' && isTechnicalDatabaseError(body)) ||
      (typeof body === 'object' &&
        body &&
        isTechnicalDatabaseError(String((body as ApiErrorBody).message ?? ''))))
  ) {
    return options.duplicateEntryMessage;
  }

  if (status && options.statusFallbacks?.[status]) {
    return options.statusFallbacks[status]!;
  }

  return fallback;
}

export const USER_CREATE_STATUS_FALLBACKS: Partial<Record<number, string>> = {
  400: 'Unable to save user. Please verify the entered details and try again.',
  409: 'User already exist in database',
  500: 'User already exist in database',
};

export const USER_CREATE_DUPLICATE_MESSAGE = 'User already exist in database';

export const CUSTOMER_CREATE_STATUS_FALLBACKS: Partial<Record<number, string>> =
  {
    400:
      'Unable to create customer. Please verify the entered details and try again.',
    409: 'Customer already exist in database',
    500: 'Customer already exist in database',
  };

export const CUSTOMER_CREATE_DUPLICATE_MESSAGE =
  'Customer already exist in database';
